import React from "react";
import ImageCropped from "views_shared/app/javascript/components/ImageCropped";
import { formatRelativeVerbose } from "views_shared/app/javascript/utils/dateFormatters";

/**
 * Get badge styling for profile type
 * Uses the same colors as AvatarWithLabel for consistency
 */
// Steel blue "garish" color used for admin styling throughout the app
const COLOR_ADMIN = "#4682B4";

function getProfileTypeBadge(profileType, isAdmin, isSuperadmin) {
  if (isSuperadmin) {
    return {
      label: (
        <>
          <i className="fa fa-star me-1" />
          Superadmin
        </>
      ),
      backgroundColor: COLOR_ADMIN,
      textColor: "white",
    };
  }
  if (isAdmin) {
    return {
      label: (
        <>
          <i className="fa fa-shield me-1" />
          Admin
        </>
      ),
      backgroundColor: COLOR_ADMIN,
      textColor: "white",
    };
  }

  switch (profileType) {
    case "type_media":
      return {
        label: "Media",
        backgroundColor: globalThis.Qwoted.COLOR_MEDIA,
        textColor: "white",
      };
    case "type_promoter_pr":
      return {
        label: "PR",
        backgroundColor: globalThis.Qwoted.COLOR_PR,
        textColor: "white",
      };
    case "type_promoter_expert":
      return {
        label: "Expert",
        backgroundColor: globalThis.Qwoted.COLOR_EXPERT,
        textColor: "white",
      };
    case "type_promoter_hybrid":
      return {
        label: "Hybrid",
        backgroundColor: globalThis.Qwoted.COLOR_EXPERT_PR,
        textColor: "white",
      };
    default:
      return {
        label: "Unknown",
        backgroundColor: "#6c757d",
        textColor: "white",
      };
  }
}

function autoActionBadge(autoAction, effectiveAction) {
  const action = autoAction || effectiveAction;
  if (!action) return null;

  const isSynthesized = !autoAction && effectiveAction;
  const style = isSynthesized
    ? { border: "1px dashed #6c757d", color: "#6c757d", backgroundColor: "transparent" }
    : { border: "1px solid #6c757d", color: "#6c757d", backgroundColor: "transparent" };

  if (action === "ghost") {
    return (
      <span className="badge" style={style} title={isSynthesized ? "Synthesized from user status" : "Explicit config"}>
        Ghost{isSynthesized ? " (disabled)" : ""}
      </span>
    );
  }
  if (action === "manual_review") {
    return (
      <span className="badge" style={style} title={isSynthesized ? "Synthesized from user status" : "Explicit config"}>
        Manual Review{isSynthesized ? " (default)" : ""}
      </span>
    );
  }
  if (action === "defer") {
    return (
      <span className="badge" style={style} title="Explicit config">
        Defer to Outlet
      </span>
    );
  }
  return null;
}

function AdminUserProfile({ profile, userType }) {
  const permissions = profile.permissions ?? 0;
  const isSuperadmin = permissions === 99;
  const isAdmin = permissions >= 1;
  const isMedia = profile.type_of_profile === "type_media";

  const badge = getProfileTypeBadge(profile.type_of_profile, isAdmin, isSuperadmin);

  // Payment plan display (only for promoters, not media or admins)
  let displayPaymentPlan = null;
  if (!isMedia && !isAdmin) {
    displayPaymentPlan = profile.payment_plan ? profile.payment_plan : "free";
  }

  const adminDetailLink = `/admin/users/${profile.slug}`;

  return (
    <tr>
      <td className="py-3 px-2">
        <div className="d-flex align-items-center">
          <ImageCropped
            roundedCircle
            wrapperClasses="me-3"
            src={profile.profile_image_url}
            alt={profile.headline}
            size={50}
          />
          <div className="d-flex flex-column">
            <a href={adminDetailLink} className="fw-bold text-decoration-none">
              {profile.name}
            </a>
            <small className="text-muted">{profile.headline}</small>
          </div>
        </div>
      </td>
      <td className="py-3 px-2 align-middle">
        <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
          <span
            className="badge"
            style={{
              backgroundColor: badge.backgroundColor,
              color: badge.textColor,
            }}
          >
            {badge.label}
          </span>
          {/* Auto-action badge for media profiles */}
          {userType === "media" && autoActionBadge(profile.auto_action, profile.effective_profile_action)}
          {displayPaymentPlan && (
            <span className="text-muted small">
              <i className="fa fa-credit-card me-1" />
              {displayPaymentPlan}
            </span>
          )}
          {profile.team_name && (
            <span className="text-muted small">
              <i className="fa fa-users me-1" />
              {profile.team_name}
            </span>
          )}
        </div>
        <div className="text-muted small">
          <i className="fa fa-envelope me-1" />
          {profile.email}
        </div>
      </td>
      <td className="py-3 px-2 align-middle text-muted small">
        {profile.last_active_at ? formatRelativeVerbose(profile.last_active_at) : "Never"}
      </td>
    </tr>
  );
}

export default AdminUserProfile;
